import { hexToRGB } from '@repo-level/core-front';
import { Link } from 'react-navi';
import styled, { css, keyframes } from 'styled-components';

import type { ButtonTypeStyles } from './helpers';

const shimmerAnimate = keyframes`
  0% {
    transform: translateX(-100%);
    opacity: 0;
  }
  50% {
    transform: translateX(0%);
    opacity: 0.7;
  }
  100% {
    transform: translateX(100%);
    opacity: 0;
  }
`;

type swipeToIconType = {
  reason?: boolean;
  icon: JSX.Element;
};

export interface ScButtonProps {
  green?: boolean;
  neonGreen?: boolean;
  blue?: boolean;
  gray?: boolean;
  goldGradient?: boolean;
  darkGray?: boolean;
  lightGray?: boolean;
  graphiteGray?: boolean;
  grayGradient?: boolean;
  grayDarker?: boolean;
  blueTransparent?: boolean;
  greenTransparent?: boolean;
  whiteTransparent?: boolean;
  grayTransparent?: boolean;
  lavenderGradient?: boolean;
  whiteTransparentNoBorder?: boolean;
  pinkTransparent?: boolean;

  small?: boolean;
  medium?: boolean;
  large?: boolean;
  xLarge?: boolean;
  xxLarge?: boolean;

  link?: boolean;
  disable?: boolean;
  autoPaddingHorizontal?: boolean;
  wide?: boolean;
  paddingX?: number;
  fontSize?: number;

  shimmer?: boolean;

  pending?: boolean;

  _width?: number;
  paddingHorizontal?: number;

  animated?: boolean;

  swipeToIcon?: swipeToIconType;

  buttonTypeStyles?: ButtonTypeStyles;
}

const shine = keyframes`
	0% ,15%{left: -400%}
	25% {left: 400%}
	100% {left: 400%}
`;
const scale = keyframes`
	0%, 5%, 10%,15%, 25%, 40%, 100% {transform: scale(1);}
	7% {transform: scale(1.025);}
	13% {transform: scale(1.015);}
`;

export const ScLink = styled(Link)`` as any as typeof Link;

export const ScButton = styled.button<ScButtonProps>`
  display: inline-block;
  overflow: hidden;
  position: relative;
  border: none;
  font-weight: 700;
  width: fit-content;
  text-transform: uppercase;
  background: none;
  text-align: center;
  cursor: pointer;
  letter-spacing: 0.01em;
  font-size: 14px;
  line-height: 1.14;

  svg {
    will-change: transform;
  }

  &:disabled {
    cursor: default;
    opacity: 0.2;
  }

  border-radius: ${({ large, xLarge, xxLarge }) =>
    large || xLarge || xxLarge ? '12px' : '8px'};

  // sizes
  ${({ small, medium, large, xLarge, xxLarge }) => {
    if (small)
      return css`
        height: 28px;
        padding: 0 12px;
      `;

    if (medium)
      return css`
        padding: 0 16px;
        height: 32px;
      `;

    if (large)
      return css`
        height: 40px;
        padding: 0 16px;
      `;

    if (xLarge)
      return css`
        height: 48px;
        padding: 0 16px;
        font-size: 16px;
        line-height: 1.25;
      `;

    if (xxLarge != null)
      return css`
        height: 56px;
        padding: 0 16px;
        font-size: 16px;
        line-height: 1.25;
      `;

    return css`
      height: 28px;
      padding: 0 12px;
    `;
  }}

  ${({ fontSize }) => {
    if (fontSize)
      return css`
        font-size: ${fontSize}px;
      `;
  }}

  ${({ _width }) => {
    if (_width != null)
      return css`
        min-width: ${_width}px;
      `;
  }}

	${({ paddingHorizontal }) => {
    if (paddingHorizontal != null)
      return css`
        padding: 0 ${paddingHorizontal}px;
      `;
  }}


  ${({ wide, autoPaddingHorizontal }) => {
    if (wide)
      return css`
        width: 100%;
      `;

    if (autoPaddingHorizontal != null) {
      return css`
        min-width: auto;
        width: auto;
        padding-left: 17px;
        padding-right: 17px;
      `;
    }
  }}

  ${({ paddingX }) =>
    paddingX &&
    css`
      padding: ${`0 ${paddingX}px`};
    `}

    ${({ shimmer }) =>
      shimmer &&
      css`
      &::after {
        content: '';
        position: absolute;
        top: 0;
        left: -150%;
        width: 200%;
        height: 100%;
        background: linear-gradient(
          120deg,
          transparent 0%,
          rgba(255, 255, 255, 0.8) 50%,
          transparent 100%
        );
        transform: skewX(-20deg);
        animation: ${shimmerAnimate} 2.5s linear infinite;
        pointer-events: none;
        z-index: 2;
      }
    `}

  ${({ animated }) =>
    animated &&
    css`
      animation: ${scale} 10s ease infinite;
      overflow: hidden;
      &:after {
        animation: ${shine} 10s infinite 0s;
        content: '';
        top: -500px;
        bottom: -500px;
        transform: rotate(45deg);
        width: 300%;
        position: absolute;
        z-index: 1;
        background: ${({ theme }) => `linear-gradient(
          to right,
          ${hexToRGB(theme.whiteColor, 0)} 0%,
          ${hexToRGB(theme.whiteColor, 1)} 100%
        )`};
      }
    `}

  .btn-content {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: center;
    align-items: center;
    position: relative;
    z-index: 1;
    white-space: nowrap;
    opacity: ${({ pending }) => (pending ? '0' : '1')};

    ${({ small, medium, large, xLarge }) => {
      if (small)
        return css`
          gap: 6px;
        `;

      if (medium || large)
        return css`
          gap: 8px;
        `;

      if (xLarge != null)
        return css`
          gap: 12px;
        `;

      return css`
        gap: 6px;
      `;
    }}
  }

  // text color
  color: ${({ buttonTypeStyles, theme }) =>
    buttonTypeStyles?.default.textColor || theme.whiteColor};

  //icons color
  path {
    fill: ${({ buttonTypeStyles, theme }) =>
      buttonTypeStyles?.default.textColor || theme.whiteColor};
  }

  .btn-bg {
    position: absolute;
    border-radius: ${({ large, xLarge, xxLarge }) =>
      large || xLarge || xxLarge ? '12px' : '8px'};
    inset: 0;
    z-index: 1;

    // background color
    background: ${({ buttonTypeStyles }) => {
      if (!buttonTypeStyles) return 'none';
      const {
        default: {
          backgroundGradient: { from, to },
        },
      } = buttonTypeStyles;

      return `linear-gradient(
        90deg,
        ${hexToRGB(from.color, from.opacity)},
        ${hexToRGB(to.color, to.opacity)}
      )`;
    }};

    // border color
    &::before {
      content: '';
      position: absolute;
      inset: 0;
      border-radius: ${({ large, xLarge, xxLarge }) =>
        large || xLarge || xxLarge ? '12px' : '8px'};
      padding: 2px;
      -webkit-mask: linear-gradient(#fff 0 0) content-box,
        linear-gradient(#fff 0 0);
      -webkit-mask-composite: xor;
      mask-composite: exclude;
      background: ${({ buttonTypeStyles }) => {
        if (!buttonTypeStyles) return 'none';
        const {
          default: {
            borderGradient: { from, to },
          },
        } = buttonTypeStyles;
        return `linear-gradient(
          90deg,
          ${hexToRGB(from.color, from.opacity)},
          ${hexToRGB(to.color, to.opacity)}
        )`;
      }};
    }

    // hovered version of button, which is hidden under main layer
    &::after {
      content: '';
      position: absolute;
      inset: 0;
      z-index: -1;
      opacity: 0;
      transition: opacity 0.4s;
      border-radius: ${({ large, xLarge, xxLarge }) =>
        large || xLarge || xxLarge ? '12px' : '8px'};

      background: ${({ buttonTypeStyles }) => {
        if (!buttonTypeStyles) return 'none';
        const {
          hover: {
            isDarkGradient,
            backgroundGradient: { from, to },
          },
        } = buttonTypeStyles;

        return `${
          isDarkGradient
            ? 'linear-gradient(0deg, rgba(17, 19, 24, 0.4), rgba(17, 19, 24, 0.4)),'
            : ''
        } linear-gradient(90deg, ${hexToRGB(
          from.color,
          from.opacity
        )} 0%, ${hexToRGB(to.color, to.opacity)} 100%)`;
      }};
    }
  }

  ${({ disabled, buttonTypeStyles, theme }) =>
    !disabled &&
    css`
      @media (hover: hover) {
        &:hover {
          // possible icons
          path {
            fill: ${buttonTypeStyles?.hover.textColor || theme.whiteColor};
          }

          // hover for ArrowButton wrapper
          .arrow:not(.bottom) {
            svg {
              transform: translateX(4px);
            }
          }

          // text color hover
          color: ${buttonTypeStyles?.hover.textColor || theme.whiteColor};

          .btn-bg {
            // make visible hovered version of button
            &::after {
              opacity: 1;
            }

            // border color hover
            &::before {
              background: ${() => {
                if (!buttonTypeStyles) return 'none';
                const {
                  hover: {
                    borderGradient: { from, to },
                  },
                } = buttonTypeStyles;

                return `linear-gradient(
            90deg,
            ${hexToRGB(from.color, from.opacity)},
            ${hexToRGB(to.color, to.opacity)}
          )`;
              }};
            }
          }
        }
      }
    `}
`;
